import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type { ProductAttributeSummary } from '@aeris/contracts'
import { Button, EmptyState, Input, Spinner } from '@aeris/ui'

import {
  ApiError,
  fetchProductAttributes,
  setProductAttributes,
} from '../lib/api.ts'

import { ProvenanceChip } from './provenance-chip.tsx'

/**
 * A ficha técnica do produto (F108).
 *
 * Grava na hora, como as aplicações (F031) e as fotos logo abaixo dela no
 * mesmo diálogo — e não junto do botão Salvar do formulário. É a coerência que
 * importa aqui: três blocos vizinhos, um deles esperando o Salvar e dois não,
 * seria a tela ensinando duas regras para a mesma pergunta.
 *
 * Cada escrita manda a lista INTEIRA (`PUT`), que é o que o contrato diz: "os
 * atributos deste produto são estes". Calcular a diferença no navegador seria
 * dar ao cliente a chance de errar em silêncio — uma característica apagada na
 * tela e viva no banco.
 *
 * As chaves são NOSSAS e em português comum ("Voltagem", "Cor"); o servidor
 * normaliza para `voltagem` e `cor`. O que cada marketplace chama disso é
 * tradução do adaptador, e não entra no cadastro da loja.
 */

export type ProductAttributesProps = {
  productId: string
  canWrite: boolean
}

type Draft = { key: string; value: string; unit: string }

const toInput = (attribute: ProductAttributeSummary): Draft => ({
  key: attribute.key,
  value: attribute.value,
  unit: attribute.unit ?? '',
})

export const ProductAttributes = ({
  productId,
  canWrite,
}: ProductAttributesProps): ReactNode => {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<Draft>({ key: '', value: '', unit: '' })
  const [error, setError] = useState<string | null>(null)

  const attributes = useQuery({
    queryKey: ['product-attributes', productId],
    queryFn: async ({ signal }) =>
      await fetchProductAttributes(productId, signal),
  })

  const rows = attributes.data?.attributes ?? []

  const save = useMutation({
    mutationFn: async (next: readonly Draft[]) =>
      await setProductAttributes(productId, {
        attributes: next.map((entry) => ({
          key: entry.key,
          value: entry.value,
          unit: entry.unit,
        })),
      }),
    onSuccess: (result) => {
      setError(null)
      queryClient.setQueryData(['product-attributes', productId], result)
    },
    onError: (failure: unknown) => {
      /*
       * A frase do servidor, e não uma genérica: as recusas desta rota nomeiam
       * a característica repetida ou a chave que não serve, e é essa palavra
       * que diz o que corrigir.
       */
      setError(
        failure instanceof ApiError
          ? failure.message
          : 'Não foi possível salvar a ficha técnica.'
      )
    },
  })

  const add = (): void => {
    if (draft.key.trim().length === 0 || draft.value.trim().length === 0) return
    save.mutate([...rows.map(toInput), draft], {
      onSuccess: () => {
        setDraft({ key: '', value: '', unit: '' })
      },
    })
  }

  const remove = (key: string): void => {
    save.mutate(rows.filter((row) => row.key !== key).map(toInput))
  }

  const commit = (key: string, next: Draft): void => {
    const current = rows.find((row) => row.key === key)
    if (current === undefined) return
    // Sem mudança, sem escrita: um blur não é uma decisão.
    if (current.value === next.value && (current.unit ?? '') === next.unit) {
      return
    }
    save.mutate(rows.map((row) => (row.key === key ? next : toInput(row))))
  }

  return (
    <div className="flex flex-col gap-3">
      {error === null ? null : (
        <p
          role="alert"
          className="rounded-(--radius-md) border border-(--danger) bg-(--danger-subtle) px-3 py-2 text-(length:--text-xs) text-(--danger)"
        >
          {error}
        </p>
      )}

      {attributes.isPending ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Sem ficha técnica"
          description="Voltagem, cor, material, medida. É o que o marketplace pede na categoria e o que o cliente procura no filtro."
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.map((row) => (
            <AttributeRow
              // Remontada quando a chave muda: é o que faz o valor inicial
              // estar certo sem um efeito corrigindo o estado depois.
              key={row.key}
              attribute={row}
              canWrite={canWrite}
              busy={save.isPending}
              onCommit={(next) => {
                commit(row.key, next)
              }}
              onRemove={() => {
                remove(row.key)
              }}
            />
          ))}
        </ul>
      )}

      {canWrite ? (
        <div className="flex flex-wrap items-end gap-2">
          <Input
            aria-label="Característica"
            placeholder="Voltagem"
            value={draft.key}
            onChange={(event) => {
              setDraft({ ...draft, key: event.target.value })
            }}
            className="w-40"
          />
          <Input
            aria-label="Valor"
            placeholder="220"
            value={draft.value}
            onChange={(event) => {
              setDraft({ ...draft, value: event.target.value })
            }}
            className="w-40"
          />
          <Input
            aria-label="Unidade"
            placeholder="V"
            value={draft.unit}
            onChange={(event) => {
              setDraft({ ...draft, unit: event.target.value })
            }}
            className="w-24"
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            loading={save.isPending}
            onClick={add}
          >
            Adicionar
          </Button>
        </div>
      ) : null}
    </div>
  )
}

type AttributeRowProps = {
  attribute: ProductAttributeSummary
  canWrite: boolean
  busy: boolean
  onCommit: (next: Draft) => void
  onRemove: () => void
}

const AttributeRow = ({
  attribute,
  canWrite,
  busy,
  onCommit,
  onRemove,
}: AttributeRowProps): ReactNode => {
  const [value, setValue] = useState(attribute.value)
  const [unit, setUnit] = useState(attribute.unit ?? '')

  return (
    <li className="flex flex-wrap items-center gap-2">
      <span className="w-40 shrink-0 text-(length:--text-sm) text-(--text-secondary)">
        {attribute.key}
      </span>
      <Input
        aria-label={`Valor de ${attribute.key}`}
        value={value}
        disabled={!canWrite || busy}
        onChange={(event) => {
          setValue(event.target.value)
        }}
        onBlur={() => {
          onCommit({ key: attribute.key, value, unit })
        }}
        className="w-40"
      />
      <Input
        aria-label={`Unidade de ${attribute.key}`}
        value={unit}
        disabled={!canWrite || busy}
        onChange={(event) => {
          setUnit(event.target.value)
        }}
        onBlur={() => {
          onCommit({ key: attribute.key, value, unit })
        }}
        className="w-24"
      />
      <ProvenanceChip
        provenance={
          attribute.source === null
            ? null
            : {
                source: attribute.source,
                confidence: attribute.confidence,
                runId: null,
                at: attribute.at,
              }
        }
      />
      {canWrite ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          // Nomeado: uma coluna de lixeiras idênticas é lida por um leitor de
          // tela como "botão, botão, botão".
          aria-label={`Remover ${attribute.key}`}
          disabled={busy}
          onClick={onRemove}
        >
          <Trash2 aria-hidden="true" />
        </Button>
      ) : null}
    </li>
  )
}
